import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <SEO 
        title="Cookie Policy"
        description="Cookie policy for Strider (Strider). Learn about the cookies we use and how to manage your preferences."
        canonical="/cookies"
      />
      <Header />
      
      <main className="flex-1 pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: March 2026</p>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. What Are Cookies?</h2>
              <p className="text-gray-600">
                Cookies are small text files that are stored on your computer or mobile device when you visit 
                a website. They are widely used to make websites work more efficiently and to provide information 
                to the owners of the site.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Cookies</h2>
              <p className="text-gray-600">Strider (Strider) uses cookies for the following purposes:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                <li><strong>Marketing Cookies:</strong> Track visitors across websites for advertising purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Types of Cookies We Use</h2>
              
              <div className="mt-4 space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Essential Cookies</h3>
                  <p className="text-sm text-gray-600">
                    These cookies are necessary for the website to function and cannot be switched off. They are 
                    usually set in response to actions you take, such as setting your privacy preferences, logging 
                    in, or filling in forms.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Performance Cookies</h3>
                  <p className="text-sm text-gray-600">
                    These cookies allow us to count visits and traffic sources so we can measure and improve the 
                    performance of our site. They help us know which pages are the most and least popular and see 
                    how visitors move around the site.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Functional Cookies</h3>
                  <p className="text-sm text-gray-600">
                    These cookies enable the website to provide enhanced functionality and personalization. They 
                    may be set by us or by third-party providers whose services we have added to our pages.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Targeting Cookies</h3>
                  <p className="text-sm text-gray-600">
                    These cookies may be set through our site by our advertising partners. They may be used by 
                    those companies to build a profile of your interests and show you relevant adverts on other sites.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Third-Party Cookies</h2>
              <p className="text-gray-600">
                In addition to our own cookies, we may also use various third-party cookies to report usage 
                statistics, deliver advertisements, and so forth. These may include:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li>Google Analytics - for website analytics</li>
                <li>Stripe - for payment processing</li>
                <li>OpenStreetMap/Leaflet - for map functionality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Managing Cookies</h2>
              <p className="text-gray-600">
                You can control and manage cookies in various ways. Most browsers allow you to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li>View and delete cookies</li>
                <li>Block third-party cookies</li>
                <li>Block cookies from particular sites</li>
                <li>Block all cookies</li>
                <li>Delete all cookies when you close your browser</li>
              </ul>
              <p className="text-gray-600 mt-3">
                Please note that blocking all cookies may affect your experience on our website and prevent 
                certain features from functioning properly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Cookie Settings by Browser</h2>
              <p className="text-gray-600">To manage cookies in popular browsers:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Updates to This Policy</h2>
              <p className="text-gray-600">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, 
                or our data practices. When we make significant changes, we will notify you by posting a prominent 
                notice on our website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about our use of cookies, please contact us at{" "}
                <a href="mailto:info@strider.work" className="text-brand hover:underline">info@strider.work</a>.
              </p>
              <p className="text-gray-500 text-sm mt-4">
                Strider is operated by BOLDHORN PRESS LTD (Company No. 17134876).
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
